// Only fixed categories and application bundle coordinates leave the browser.
// Error messages, component stacks, API payloads and arbitrary URLs can contain customer data.
const ERROR_TYPES = new Set(['Error', 'TypeError', 'RangeError', 'ReferenceError', 'SyntaxError', 'URIError', 'EvalError']);
const SOURCES = new Set(['browser', 'unhandled_rejection', 'react', 'api']);
const BUNDLE_PATH = /^\/js\/(?:bundle|\d+)\.[a-f0-9]{8,64}\.js$/;

function errorMetadata(source, status) {
    const error_source = SOURCES.has(source) ? source : 'browser';
    const http_status = Number.isInteger(status) && status >= 400 && status <= 599 ? status : undefined;
    return { error_source, error_context: error_source === 'api' ? 'request_session' : 'observed_session', ...(error_source === 'api' && http_status ? { http_status } : {}) };
}

function errorMessage(source, status) {
    if (source === 'api') return status ? `API request failed (HTTP ${status})` : 'API request failed';
    if (source === 'react') return 'React render failed';
    if (source === 'unhandled_rejection') return 'Unhandled promise rejection';
    return 'Unexpected browser error';
}

function safeBundlePath(filename) {
    if (typeof filename !== 'string') return undefined;
    // Origins and query/hash values are never retained, even for a matching bundle.
    const path = filename.replace(/^https?:\/\/[^/]+/, '').split(/[?#]/)[0];
    return BUNDLE_PATH.test(path) ? path : undefined;
}

function safeFrames(frames) {
    if (!Array.isArray(frames)) return [];
    return frames.slice(-20).reduce((result, frame) => {
        if (!frame || typeof frame !== 'object') return result;
        const filename = safeBundlePath(frame.filename);
        if (filename && Number.isSafeInteger(frame.lineno) && frame.lineno > 0 && Number.isSafeInteger(frame.colno) && frame.colno > 0) {
            result.push({ platform: 'web:javascript', filename, function: '<anonymous>', in_app: true, lineno: frame.lineno, colno: frame.colno });
        }
        return result;
    }, []);
}

function sanitizedError(error, source, status) {
    const safe = new Error(errorMessage(source, status));
    safe.name = source === 'api' ? 'PartnerConnectApiError' : ERROR_TYPES.has(error && error.name) ? error.name : 'Error';
    // Keep only deployable bundle positions. Do not forward the original stack header or function names.
    const frames = [];
    if (error && typeof error.stack === 'string') {
        for (const line of error.stack.slice(0, 16000).split('\n').slice(1, 21)) {
            const match = line.match(/((?:https?:\/\/[^\s()]+)?\/js\/(?:bundle|\d+)\.[a-f0-9]{8,64}\.js)(?:[?#][^\s():]*)?:(\d+):(\d+)\)?$/);
            if (match) frames.push({ filename: match[1], lineno: Number(match[2]), colno: Number(match[3]) });
        }
    }
    safe.stack = `${safe.name}: ${safe.message}` + safeFrames(frames).map(frame => `\n    at anonymous (${frame.filename}:${frame.lineno}:${frame.colno})`).join('');
    return safe;
}

function sanitizeExceptionProperties(properties) {
    const metadata = errorMetadata(properties.error_source, properties.http_status);
    const exception = Array.isArray(properties.$exception_list) && properties.$exception_list[0];
    if (!exception || typeof exception !== 'object') return undefined;
    const type = metadata.error_source === 'api' ? 'PartnerConnectApiError' : ERROR_TYPES.has(exception.type) ? exception.type : 'Error';
    const frames = safeFrames(exception.stacktrace && exception.stacktrace.frames);
    return {
        ...metadata,
        $exception_level: 'error',
        $exception_list: [{
            type,
            value: errorMessage(metadata.error_source, metadata.http_status),
            mechanism: { type: 'generic', handled: metadata.error_source === 'api' || metadata.error_source === 'react' },
            ...(frames.length ? { stacktrace: { type: 'raw', frames } } : {}),
        }],
    };
}

module.exports = { errorMetadata, sanitizedError, sanitizeExceptionProperties };
